import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/system';
import TextInput from '../../TextFields/TextInput.js';
import DateInput from '../../TextFields/DateInput.js';
import Modal from '../Modal.js';
import Text from '../../Text.js';
import i18n from '../../../localization/i18n.js';
import tokens from '../../../localization/tokens.js';
import * as localizationMaps from '../../../localization/maps.js';
import NumberInput from '../../TextFields/NumberInput.js';
import PhoneInput from '../../TextFields/PhoneInput.js';
import * as Services from '../../../services/index.js';
import { parseInitialUserData, returnRegularInfoChanges, returnPersonalInfoChanges, returnEmploymentInfoChanges, initialErrorState, returnStatusOptions } from './parseEmployeeData.js';
import BubbleSelectBlock from '../../BubbleSelectBlock.js';
import validateForm from './validateForm.js';
const styles = {
  form: {
    display: 'flex',
    flexDirection: 'column'
  },
  row: {
    display: 'flex',
    flexDirection: 'row'
  },
  divider: {
    width: 40
  }
};

/**
 * 
 * @returns 
 */
const EmployeeDetailsModal = ({
  open,
  isEdit,
  close,
  currentEmployee,
  classData,
  divisions,
  goBack,
  currentAccountMeta,
  isAdmin = false,
  updateUser,
  addUser,
  loader,
  showTerminateMemberButton = false,
  setShowTerminationModal,
  isMasterProfile = false,
  isMasterSite = false
}) => {
  const [user, setUser] = useState(() => parseInitialUserData(currentEmployee, isEdit, classData, divisions));
  const [errorState, setErrorState] = useState(initialErrorState);
  const theme = useTheme();
  const {
    firstname,
    lastname,
    status,
    benefitsActive,
    email,
    birthdate,
    className,
    divisionName,
    hireDate,
    gender,
    title,
    coverageType,
    addressLineTwo,
    addressLineOne,
    city,
    postalCode,
    province,
    earningsType,
    salary,
    hourlyWage,
    hoursPerWeek,
    language,
    payrollID,
    waiveEligibilityPeriod,
    phone,
    maritlStat,
    studentID,
    studyStartDate,
    degree
  } = user;
  const {
    firstnameError,
    lastnameError,
    statusError,
    benefitsActiveError,
    emailError,
    birthdateError,
    classNameError,
    divisionNameError,
    hireDateError,
    genderError,
    titleError,
    coverageTypeError,
    provinceError,
    addressLineOneError,
    addressLineTwoError,
    cityError,
    postalCodeError,
    earningsTypeError,
    salaryError,
    hourlyWageError,
    hoursPerWeekError,
    waiveEligibilityPeriodError,
    employeeDetailsPersonalDetailsError,
    phoneError,
    maritlStatError,
    studentIDError,
    studyStartDateError
  } = errorState;
  const isStudent = currentAccountMeta?.isStudentGroup;
  useEffect(() => {
    setUser(parseInitialUserData(currentEmployee, isEdit, classData, divisions));
  }, [currentEmployee, isEdit]);

  /**
   * Handles input change and updates user state
   * 
   * @param {string} field - Field name to update
   * @param {string|number} value - New value for the field
   */
  const _handleInputChange = (field, value) => {
    setUser(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  /**
   * Validates the form and sets error state if validation fails
   * 
   * @returns {Promise<boolean>} True if the form is valid, false otherwise
   */
  const _validateFormAndSetErrorState = async () => {
    const formErrors = await validateForm(user, currentEmployee, isEdit, isAdmin, classData, isStudent, isMasterProfile);
    if (!formErrors) {
      return true;
    }
    setErrorState({
      ...formErrors
    });
    return false;
  };

  /**
   * Resets the form to its initial state
   */
  const _resetForm = () => {
    setErrorState(initialErrorState);
    parseInitialUserData(currentEmployee, false, classData, divisions);
  };

  /**
   * Resets the form to its initial state
   */
  const _onClose = () => {
    _resetForm();
    close();
  };

  /**
   * Saves the form data, conditionally with a change request if used in the control site, and with a regular update in the master site
   * 
   * @returns {Promise<boolean>} True if the save was successful, false otherwise
   */
  const _save = async () => {
    try {
      const isFormValid = await _validateFormAndSetErrorState();
      if (!isFormValid) {
        return false;
      }
      setErrorState(initialErrorState);

      /*
       * For admins, we need to get the types of data changes that may have occurred so that a change request or regular update can take place.
       * Edits from the master site and advisor sites go through right away, no need for a change request
       */
      if (isAdmin && isEdit) {
        const regularUpdateData = returnRegularInfoChanges(user, currentEmployee, divisions, isStudent);
        const personalInfoChanges = returnPersonalInfoChanges(user, currentEmployee);
        const employmentDetailsChanges = returnEmploymentInfoChanges(user, currentEmployee, classData, isStudent);
        updateUser(regularUpdateData, personalInfoChanges, employmentDetailsChanges);
        return true;
      }

      /*
       * These functions are used to update users from the ADVISOR and MASTER sites, and add users from all sites. Updates from the master and advisor sites MUST go through the employee modal handler first,
       * to properly set the effective date, expected return date and allow for terminations where appropriate
       */
      isEdit ? updateUser(user) : addUser(user);
      return false;
    } catch (error) {
      return false;
    }
  };

  /**
   * 
   * @param {*} input 
   */
  const _formatPostalCode = input => {
    const formattedPostalCode = input.toUpperCase().trim().replace(/\s+/g, '') // Remove all spaces
    .replace(/([A-Z0-9]{3})([A-Z0-9]{3})/, '$1 $2'); // Add a space after the first 3 characters

    _handleInputChange('postalCode', formattedPostalCode);
  };
  return /*#__PURE__*/React.createElement(Modal, {
    open: open,
    title: isEdit ? i18n.t(tokens.labels.changeRequests.editMemberDetails) : i18n.t(tokens.labels.employees.newMember),
    medium: true,
    primaryButton: {
      label: i18n.t(tokens.buttons.save),
      onClick: _save
    },
    secondaryButton: showTerminateMemberButton ? {
      label: i18n.t(tokens.buttons.terminate),
      onClick: () => setShowTerminationModal(true)
    } : null,
    goBack: goBack,
    onClose: _onClose,
    loader: loader
  }, employeeDetailsPersonalDetailsError ? /*#__PURE__*/React.createElement(Text, {
    large: true,
    light: true,
    customStyle: {
      color: theme.palette.error.main
    }
  }, isStudent ? i18n.t(tokens.labels.validations.editStudyDetailsSeparateFromPersonalDetails) : i18n.t(tokens.labels.validations.editEmployeeDetailsValidationError)) : null, isMasterProfile ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    medium: true,
    bold: true
  }, 'Benefits Details'), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.status),
    value: status,
    onChange: e => _handleInputChange('status', e.target.value),
    error: Boolean(statusError),
    helperText: statusError,
    fullWidth: true,
    required: true,
    autoFocus: !isEdit,
    select: true,
    selectOptions: returnStatusOptions(currentEmployee.isStudentGroup, Boolean(Number(currentEmployee.status) === 9)),
    disabled: currentEmployee.status === 11
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: 'Benefits Active',
    value: benefitsActive ? 'Yes' : 'No',
    onChange: e => {
      e = e.target.value === 'No' ? 0 : 1;
      return _handleInputChange('benefitsActive', e);
    },
    error: Boolean(benefitsActiveError),
    helperText: benefitsActiveError,
    fullWidth: true,
    required: true,
    select: true,
    selectOptions: [{
      key: 0,
      value: 'No'
    }, {
      key: 1,
      value: 'Yes'
    }]
  }))) : null, /*#__PURE__*/React.createElement(Text, {
    medium: true,
    bold: true
  }, i18n.t(tokens.labels.changeRequests.personalDetails)), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.firstname),
    value: firstname,
    onChange: e => _handleInputChange('firstname', e.target.value),
    error: Boolean(firstnameError),
    helperText: firstnameError,
    fullWidth: true,
    required: true,
    autoFocus: !isEdit
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.lastname),
    value: lastname,
    onChange: e => _handleInputChange('lastname', e.target.value),
    error: Boolean(lastnameError),
    helperText: lastnameError,
    fullWidth: true,
    required: true
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.email),
    value: email,
    onChange: e => _handleInputChange('email', e.target.value),
    error: Boolean(emailError),
    helperText: emailError,
    fullWidth: true,
    required: true
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.account.dateOfBirthYYYYMMDD),
    value: birthdate,
    onChange: e => _handleInputChange('birthdate', e.target.value),
    error: Boolean(birthdateError),
    helperText: birthdateError,
    fullWidth: true,
    required: true
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.gender),
    value: gender,
    onChange: e => _handleInputChange('gender', e.target.value),
    error: Boolean(genderError),
    helperText: genderError,
    fullWidth: true,
    required: true,
    select: true,
    selectOptions: Services.selectFieldOptions.genderOptions()
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.provinceOfResidence),
    value: province,
    onChange: e => _handleInputChange('province', e.target.value),
    error: Boolean(provinceError),
    helperText: provinceError,
    fullWidth: true,
    required: true,
    select: true,
    selectOptions: Services.selectFieldOptions.provinceOptions()
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.addressLineOne),
    value: addressLineOne,
    onChange: e => _handleInputChange('addressLineOne', e.target.value),
    error: Boolean(addressLineOneError),
    helperText: addressLineOneError,
    fullWidth: true,
    required: Boolean(isStudent)
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.addressLineTwo),
    value: addressLineTwo,
    onChange: e => _handleInputChange('addressLineTwo', e.target.value),
    error: Boolean(addressLineTwoError),
    fullWidth: true,
    helperText: i18n.t(tokens.labels.addressLineTwoExample)
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.account.cityTown),
    value: city,
    onChange: e => _handleInputChange('city', e.target.value),
    error: Boolean(cityError),
    helperText: cityError,
    fullWidth: true,
    required: Boolean(isStudent)
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.postalCode),
    value: postalCode,
    onChange: e => {
      // Allow typing only 6 alphanumeric characters and trim any excess
      const postalCodeValue = e.target.value.toUpperCase().slice(0, 7);
      _handleInputChange('postalCode', postalCodeValue);
    },
    fullWidth: true,
    error: Boolean(postalCodeError),
    helperText: postalCodeError,
    required: Boolean(isStudent),
    onBlur: e => _formatPostalCode(e.target.value)
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.individualDependents),
    value: coverageType,
    onChange: e => _handleInputChange('coverageType', e.target.value),
    error: Boolean(coverageTypeError),
    helperText: coverageTypeError,
    fullWidth: true,
    required: true,
    select: true,
    disabled: Boolean(isEdit && currentEmployee?.cert),
    selectOptions: Services.selectFieldOptions.coverageOptions()
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.memberLanguage),
    value: language,
    onChange: e => _handleInputChange('language', e.target.value),
    fullWidth: true,
    required: true,
    select: true,
    selectOptions: Services.selectFieldOptions.languageOptions()
  })), isEdit && isAdmin || isStudent || isMasterProfile ? /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(PhoneInput, {
    label: i18n.t(tokens.labels.phoneNumber),
    value: phone,
    onChange: e => _handleInputChange('phone', e.target.value),
    error: Boolean(phoneError),
    helperText: phoneError,
    fullWidth: true
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.maritalStatus),
    fullWidth: true,
    required: true,
    value: maritlStat,
    onChange: e => _handleInputChange('maritlStat', e.target.value),
    select: true,
    selectOptions: Services.selectFieldOptions.maritalStatusOptions(),
    error: Boolean(maritlStatError),
    helperText: maritlStatError
  })) : null, isStudent ? /*#__PURE__*/React.createElement(Text, {
    medium: true,
    bold: true,
    customStyle: {
      marginTop: 10
    }
  }, i18n.t(tokens.labels.employees.studyDetails)) : /*#__PURE__*/React.createElement(Text, {
    medium: true,
    bold: true,
    customStyle: {
      marginTop: 10
    }
  }, i18n.t(tokens.labels.employees.employmentDetails)), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.class),
    value: className,
    onChange: e => _handleInputChange('className', e.target.value),
    error: Boolean(classNameError),
    helperText: classNameError,
    fullWidth: true,
    required: true,
    select: true,
    selectOptions: Object.values(classData).map(item => item.title)
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.division),
    value: divisionName,
    onChange: e => _handleInputChange('divisionName', e.target.value),
    error: Boolean(divisionNameError),
    helperText: divisionNameError,
    fullWidth: true,
    required: true,
    select: true,
    selectOptions: Object.values(divisions).map(item => item.title)
  })),
  // these fields are not required for student groups 
  !isStudent ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.employees.hireDate),
    value: hireDate,
    onChange: e => _handleInputChange('hireDate', e.target.value),
    error: Boolean(hireDateError),
    helperText: hireDateError,
    fullWidth: true,
    required: true
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.occupation),
    value: title,
    onChange: e => _handleInputChange('title', e.target.value),
    error: Boolean(titleError),
    helperText: titleError,
    fullWidth: true,
    required: true
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.earningsType),
    value: earningsType,
    onChange: e => _handleInputChange('earningsType', e.target.value),
    error: Boolean(earningsTypeError),
    helperText: earningsTypeError,
    fullWidth: true,
    required: true,
    select: true,
    selectOptions: Services.selectFieldOptions.earningsTypeOptions()
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), localizationMaps.toEnglish("earningsTypes", earningsType) === 'Hourly' ? /*#__PURE__*/React.createElement(NumberInput, {
    label: i18n.t(tokens.labels.employees.hourlyRate),
    prefix: "$ ",
    value: hourlyWage,
    onChange: e => _handleInputChange('hourlyWage', e.target.value),
    error: Boolean(hourlyWageError),
    helperText: hourlyWageError,
    fullWidth: true,
    required: true
  }) : /*#__PURE__*/React.createElement(NumberInput, {
    label: i18n.t(tokens.labels.employees.salary),
    prefix: "$ ",
    value: salary,
    onChange: e => _handleInputChange('salary', e.target.value),
    error: Boolean(salaryError),
    helperText: salaryError,
    fullWidth: true,
    required: true
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(NumberInput, {
    label: i18n.t(tokens.labels.employees.hoursPerWeek),
    value: hoursPerWeek,
    onChange: e => _handleInputChange('hoursPerWeek', e.target.value),
    error: Boolean(hoursPerWeekError),
    helperText: hoursPerWeekError,
    fullWidth: true,
    required: true
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.payrollID),
    value: payrollID,
    onChange: e => _handleInputChange('payrollID', e.target.value),
    fullWidth: true
  }))) : null,
  // we require study start date, degree, and student id when adding a new student to a student group
  isStudent ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.employees.studyStartDate),
    value: studyStartDate,
    onChange: e => _handleInputChange('studyStartDate', e.target.value),
    error: Boolean(studyStartDateError),
    helperText: studyStartDateError,
    fullWidth: true,
    required: true
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.employees.degree),
    value: degree,
    onChange: e => _handleInputChange('degree', e.target.value),
    fullWidth: true
  })), /*#__PURE__*/React.createElement("div", {
    style: styles.row
  }, /*#__PURE__*/React.createElement(NumberInput, {
    label: i18n.t(tokens.labels.employees.studentID),
    value: studentID,
    onChange: e => _handleInputChange('studentID', e.target.value),
    error: Boolean(studentIDError),
    helperText: studentIDError,
    fullWidth: true,
    required: true
    // do not allow this field to be edited once set, except for in the census upload stage before the account is set live
    ,
    disabled: isEdit && currentAccountMeta?.status !== 0
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.divider
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      width: '100%'
    }
  }))) : null,
  // we only allow this when the account is live OR if editing from the master site, if it's pending or in revisions then it's census additions and should be waived automatically by the backend systems
  currentAccountMeta?.status === 1 || isMasterSite ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    medium: true,
    bold: true,
    display: "block",
    align: "left",
    margin: "8px 0 4px"
  }, i18n.t(tokens.labels.employees.youMustChooseToWaive)), /*#__PURE__*/React.createElement(Text, {
    medium: true,
    display: "block",
    align: "left",
    margin: "4px 0 8px"
  }, i18n.t(tokens.labels.employees.waivingMeansThat)), /*#__PURE__*/React.createElement(BubbleSelectBlock, {
    disabled: !isAdmin && !isMasterSite && isEdit // this is disabled for advisors who are editing members
    ,
    customStyle: {
      marginTop: "8px",
      marginLeft: "8px"
    },
    allowToggleOff: true,
    first: {
      title: i18n.t(tokens.buttons.doNotWaive),
      selected: !waiveEligibilityPeriod,
      field: 'doNotWaive',
      customStyle: {
        fontSize: 12
      }
    },
    second: {
      title: i18n.t(tokens.buttons.waive),
      selected: waiveEligibilityPeriod,
      field: 'waive',
      customStyle: {
        fontSize: 12
      }
    },
    handleChange: field => {
      if (field === 'doNotWaive') {
        _handleInputChange('waiveEligibilityPeriod', false);
      } else if (field === 'waive') {
        _handleInputChange('waiveEligibilityPeriod', true);
      }
    }
  }), waiveEligibilityPeriodError && /*#__PURE__*/React.createElement(Text, {
    medium: true,
    display: "block",
    align: "left",
    margin: "4px 0",
    color: "red"
  }, i18n.t(tokens.labels.employees.chooseToWaiveError))) : null, /*#__PURE__*/React.createElement("span", {
    id: "census-form"
  }));
};
export default EmployeeDetailsModal;